import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks while preserving safe formatting
 * @param {string} html - Raw HTML content
 * @param {Object} options - Sanitization options
 * @returns {string} - Sanitized HTML
 */

// Clean up HTML from CMS/Word paste before sanitization
const cleanCmsHtml = (htmlString) => {
  let html = htmlString;

  // 1. Strip font-related inline style properties (font-family, font-size, line-height, font)
  //    Use separate patterns for double-quoted and single-quoted style attributes
  html = html.replace(/style="([^"]*)"/gi, (_match, styles) => {
    const cleaned = styles
      .split(';')
      .filter(rule => {
        const prop = rule.split(':')[0]?.trim().toLowerCase() || '';
        return !['font-family', 'font-size', 'line-height', 'font'].includes(prop);
      })
      .join(';')
      .trim();
    return cleaned ? `style="${cleaned}"` : '';
  });

  html = html.replace(/style='([^']*)'/gi, (_match, styles) => {
    const cleaned = styles
      .split(';')
      .filter(rule => {
        const prop = rule.split(':')[0]?.trim().toLowerCase() || '';
        return !['font-family', 'font-size', 'line-height', 'font'].includes(prop);
      })
      .join(';')
      .trim();
    return cleaned ? `style="${cleaned}"` : '';
  });

  // 2. Convert h1/h2 tags to div (CMS content should not contain top-level headings,
  //    headings are provided by the page components)
  html = html.replace(/<h1(\s|>)/gi, '<div$1');
  html = html.replace(/<\/h1>/gi, '</div>');
  html = html.replace(/<h2(\s|>)/gi, '<div$1');
  html = html.replace(/<\/h2>/gi, '</div>');

  // 3. Remove MS Word classes (MsoNormal, MsoListParagraph, etc.)
  html = html.replace(/\s*class="Mso[^"]*"/gi, '');

  return html;
};

export const sanitizeHTML = (html, options = {}) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // First clean up CMS/Word formatting from the raw HTML
  const cleanedHtml = cleanCmsHtml(html);

  const defaultConfig = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'img',
      'blockquote', 'pre', 'code'
    ],
    ALLOWED_ATTR: [
      'class', 'id', 'style', 'href', 'title', 'alt', 'src', 'target'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|ftp):\/\/|mailto:|tel:|#)/i,
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false
  };

  const config = { ...defaultConfig, ...options };

  return DOMPurify.sanitize(cleanedHtml, config);
};

/**
 * Secure content component for rendering sanitized HTML
 * @param {Object} props - Component props
 * @param {string} props.content - HTML content to sanitize and render
 * @param {string} props.className - CSS classes
 * @param {Object} props.sanitizeOptions - DOMPurify options
 * @returns {JSX.Element} - Safe content component
 */
export const SecureContent = ({
  content,
  className = '',
  sanitizeOptions = {},
  ...otherProps
}) => {
  const sanitizedContent = sanitizeHTML(content, sanitizeOptions);

  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      {...otherProps}
    />
  );
};
